import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../../context/AppContext';
import { authorizationApi } from '../../../services/authorizationApiService';
import RoleFormModal from './components/RoleFormModal';
import ManagePoliciesModal from './components/ManagePoliciesModal';
import Button from '../../common/Button/Button';
import SearchBar from '../../common/SearchBar/SearchBar';
import Table from '../../common/Table/Table';

export default function RolesTab({ onRefresh }) {
  const { showToast } = useAppContext();
  const [roles, setRoles] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRoleForPolicies, setSelectedRoleForPolicies] = useState(null);
  const [showPolicyAttachModal, setShowPolicyAttachModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [rolesData, policiesData] = await Promise.all([
        authorizationApi.getRoles(),
        authorizationApi.getPolicies()
      ]);
      // Extract arrays from response objects
      setRoles(rolesData?.roles || []);
      setPolicies(policiesData?.policies || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setShowFormModal(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setShowFormModal(true);
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Are you sure you want to delete the role "${role.role_name}"? This action cannot be undone.`)) {
      return;
    }

    // Errors handled automatically by apiClient
    await authorizationApi.deleteRole(role.role_id);
    await loadData();
    if (onRefresh) onRefresh();
    showToast('Role deleted successfully', 'success');
  };

  const handleSaveRole = async (roleData) => {
    // Errors handled automatically by apiClient
    if (editingRole) {
      await authorizationApi.updateRole(editingRole.role_id, roleData);
    } else {
      await authorizationApi.createRole(roleData);
    }
    setShowFormModal(false);
    setEditingRole(null);
    await loadData();
    if (onRefresh) onRefresh();
  };

  const handleManagePolicies = (role) => {
    setSelectedRoleForPolicies(role);
    setShowPolicyAttachModal(true);
  };

  const handleAttachPolicy = async (policyId) => {
    if (!selectedRoleForPolicies) return;

    const currentRoleId = selectedRoleForPolicies.role_id;

    // Errors handled automatically by apiClient
    await authorizationApi.attachPolicy(currentRoleId, policyId);
    
    // Reload data
    const [rolesData, policiesData] = await Promise.all([
      authorizationApi.getRoles(),
      authorizationApi.getPolicies()
    ]);
    const updatedRoles = rolesData?.roles || [];
    const updatedPolicies = policiesData?.policies || [];
    
    // Find the updated role
    const updatedRole = updatedRoles.find(r => r.role_id === currentRoleId);
    
    // Update state in the correct order to prevent modal from closing
    if (updatedRole) {
      setSelectedRoleForPolicies(updatedRole);
    }
    setRoles(updatedRoles);
    setPolicies(updatedPolicies);
  };

  const handleDetachPolicy = async (policyId) => {
    if (!selectedRoleForPolicies) return;

    const currentRoleId = selectedRoleForPolicies.role_id;

    // Errors handled automatically by apiClient
    await authorizationApi.detachPolicy(currentRoleId, policyId);
    
    // Reload data
    const [rolesData, policiesData] = await Promise.all([
      authorizationApi.getRoles(),
      authorizationApi.getPolicies()
    ]);
    const updatedRoles = rolesData?.roles || [];
    const updatedPolicies = policiesData?.policies || [];
    
    // Find the updated role
    const updatedRole = updatedRoles.find(r => r.role_id === currentRoleId);
    
    // Update state in the correct order to prevent modal from closing
    if (updatedRole) {
      setSelectedRoleForPolicies(updatedRole);
    }
    setRoles(updatedRoles);
    setPolicies(updatedPolicies);
  };

  const filteredRoles = roles.filter(role =>
    role.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.role_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      header: 'Role Name',
      accessor: 'role_name',
      width: '180px',
      render: (role) => <span className="name-col">{role.role_name}</span>
    },
    {
      header: 'Description',
      accessor: 'description',
      render: (role) => <span className="description-col">{role.description}</span>
    },
    {
      header: 'Policies',
      width: '140px',
      render: (role) => (
        <span className="count-text">
          {role.policies?.length || 0} {role.policies?.length === 1 ? 'policy' : 'policies'}
        </span>
      )
    },
    {
      header: 'Actions',
      width: '250px',
      render: (role) => (
        <div className="action-buttons">
          <Button
            variant="secondary"
            size="sm"
            icon="fas fa-link"
            onClick={() => handleManagePolicies(role)}
          >
            Policies
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon="fas fa-edit"
            onClick={() => handleEditRole(role)}
          />
          <Button
            variant="danger"
            size="sm"
            icon="fas fa-trash"
            onClick={() => handleDeleteRole(role)}
          />
        </div>
      )
    }
  ];

  if (error) {
    return (
      <div className="error-state">
        <i className="fas fa-exclamation-circle"></i>
        <h4>Error Loading Roles</h4>
        <p>{error}</p>
        <Button variant="primary" icon="fas fa-redo" onClick={loadData}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="roles-tab">
      <div className="roles-actions">
        <div className="search-wrapper">
          <SearchBar
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button variant="secondary" icon="fas fa-redo" onClick={loadData} />
        <Button variant="primary" icon="fas fa-plus" onClick={handleCreateRole}>
          Create Role
        </Button>
      </div>

      <Table
        columns={columns}
        data={filteredRoles}
        isLoading={loading}
        emptyMessage={searchTerm ? 'No roles match your search criteria.' : 'Create your first role to get started.'}
        emptyIcon="fas fa-user-tag"
      />

      {showFormModal && (
        <RoleFormModal
          role={editingRole}
          onSave={handleSaveRole}
          onClose={() => {
            setShowFormModal(false);
            setEditingRole(null);
          }}
        />
      )}

      {showPolicyAttachModal && selectedRoleForPolicies && (
        <ManagePoliciesModal
          role={selectedRoleForPolicies}
          policies={policies}
          onAttach={handleAttachPolicy}
          onDetach={handleDetachPolicy}
          onClose={() => { setShowPolicyAttachModal(false); }}
        />
      )}
    </div>
  );
}
